import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import ContactSheet from "@/components/ContactSheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { Drawer, DrawerContent } from "@/components/ui/drawer";

const offers = [
{
  title: "Reduced Rate Website — 30% Off",
  description:
  "For businesses that need more than a landing page.\n\nIncludes:\n· Strategy + sitemap planning\n· 5–10 custom-designed pages\n· Framer build + responsive development\n· Basic CMS setup (blog, menu, product catalog, etc.)\n· On-site SEO structure (meta titles, descriptions, page hierarchy)\n\nIdeal for:\nE-commerce stores, restaurants, pop-ups, hospitality concepts, and growing brands that need a scalable foundation.",
  offerType: "30% Discount",
  cta: "Build something good"
},
{
  title: "One-Page Website Development — $1,000 Flat Rate",
  description:
  "A focused, conversion-ready single-page site.\n\nIncludes:\n· Structured layout (hero, services/menu, about, contact)\n· Mobile-optimized Framer build\n· Contact form integration\n· Basic SEO setup\n\nPerfect for:\nLash techs, estheticians, solo founders, bars, small restaurants, and service-based businesses that need something clean and professional — fast.",
  offerType: "$1,000 One-Pager",
  cta: "Launch your site"
},
{
  title: "Barter / Trade Agreement",
  description:
  "Project exchange in lieu of full payment.\nPriority given to brands in music, hospitality, food & beverage, events, and photography.\nBusinesses rooted in live experiences and culture are especially encouraged to reach out.",
  offerType: "Barter / Trade",
  cta: "Propose a Trade"
}];

/* ── Offer detail content (reused in modal + drawer) ── */
const OfferDetail = ({ offer, onClose }: { offer: typeof offers[0]; onClose: () => void }) => (
  <div className="relative">
    <button
      onClick={onClose}
      className="absolute top-0 right-0 text-ticket-muted/60 hover:text-ticket-fg transition-colors"
      aria-label="Close"
    >
      <X size={16} />
    </button>
    <h4 className="font-display italic text-[clamp(1.25rem,3vw,1.75rem)] leading-[1.1] tracking-[-0.01em] text-ticket-fg mb-6 pr-8">
      {offer.title}
    </h4>
    <div className="flex items-center gap-3 mb-5">
      <span className="text-ticket-muted text-[10px]">✦</span>
      <span className="flex-1 border-t border-dashed border-ticket-muted/30" />
      <span className="text-ticket-muted text-[10px]">✦</span>
    </div>
    <p className="font-body text-[13px] text-ticket-muted leading-[1.8] whitespace-pre-line mb-8">
      {offer.description}
    </p>
    <ContactSheet defaultDallasLocal defaultDallasOfferType={offer.offerType}>
      <button className="inline-flex items-center gap-2 px-5 py-2.5 border border-ticket-fg text-ticket-fg font-body text-[9px] font-medium tracking-[0.15em] uppercase hover:bg-ticket-fg hover:text-ticket-bg transition-colors">
        {offer.cta}
        <ArrowUpRight size={10} />
      </button>
    </ContactSheet>
  </div>
);

/* ── Desktop modal ── */
const OfferModal = ({ offer, onClose }: { offer: typeof offers[0]; onClose: () => void }) => (
  <>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    />
    <motion.div
      initial={{ opacity: 0, scale: 0.96, y: 12 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96, y: 12 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      onClick={onClose}
    >
      <div
        className="ticket-card w-full max-w-md px-8 py-10 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ticket-grain" />
        <div className="relative z-10">
          <OfferDetail offer={offer} onClose={onClose} />
        </div>
      </div>
    </motion.div>
  </>
);

const DallasLocalsCTA = () => {
  const [selectedOffer, setSelectedOffer] = useState<typeof offers[0] | null>(null);
  const isMobile = useIsMobile();

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>

      {/* ── Ticket wrapper ── */}
      <div className="ticket-card relative overflow-hidden border-0">
        {/* Punch holes — left & right (desktop), top & bottom (mobile) */}
        <div className="ticket-punch ticket-punch--left" />
        <div className="ticket-punch ticket-punch--right" />

        {/* Subtle grain overlay */}
        <div className="ticket-grain" />

        {/* Inner content with padding that clears the punch holes */}
        <div className="relative z-10 px-8 py-10 md:px-12 md:py-14 lg:px-16 lg:py-16 border-0">
          {/* Top row — label + ticket number */}
          <div className="flex items-center justify-between mb-8 md:mb-10">
            <p className="font-body text-[9px] font-medium tracking-[0.3em] uppercase text-ticket-muted">
              Need a website, Dallas?
            </p>
            <span className="font-body text-[9px] tracking-[0.2em] uppercase text-ticket-muted">
              Community Offer No. 001
            </span>
          </div>

          {/* Two-column: copy left | perforation | offers right */}
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-8 md:gap-0">
            {/* Left — copy */}
            <div className="md:pr-10 lg:pr-14 flex flex-col justify-between">
              <div>
                <h3 className="font-display italic text-[clamp(2rem,5vw,3.25rem)] leading-[0.92] tracking-[-0.02em] text-ticket-fg mb-8">
                  Locals Only Club
                </h3>

                {/* Star divider */}
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-ticket-muted text-[10px]">✦</span>
                  <span className="flex-1 border-t border-dashed border-ticket-muted/30" />
                  <span className="text-ticket-muted text-[10px]">✦</span>
                </div>

                <div className="font-body text-[13px] md:text-[14px] text-ticket-muted leading-[1.8] space-y-5 mb-10">
                  <p>
                    We love our community, and in Dallas, that runs deep. We're
                    native to the Bishop Arts area (Oak Cliff... you know the
                    line) but all of Dallas is our home.
                  </p>
                  <p>
                    Now look... Dallas might not be the party capital of the
                    world, but it is a <em className="text-ticket-fg">people</em>{" "}
                    city, a grind city, and a creative city—the kind where people
                    create and build solely for the passion of it. We want to
                    pour into that!
                  </p>
                  <p>
                    If you're a Dallas creative and/or small business that needs a good website, hit us up. We're opening up <strong className="text-ticket-fg">Locals Only</strong> deals through the end of 2026. All websites built in <a href="https://framer.link/keish-design-studio" target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 decoration-dotted decoration-ticket-muted/40 hover:decoration-solid hover:text-ticket-fg transition-colors">Framer</a>.
                  </p>
                </div>
              </div>

            </div>

            {/* Perforated divider — desktop only */}
            <div className="hidden md:flex items-stretch justify-center px-4 lg:px-6">
              <div className="border-l border-dashed border-ticket-muted/30 h-full" />
            </div>


            {/* Right — offer list + stamp */}
            <div className="md:pl-4 lg:pl-6 flex flex-col">
              <div>
                {offers.map((offer, i) =>
                <div
                  key={i}
                  className="border-t border-dashed border-ticket-muted/20 last:border-b last:border-dashed last:border-ticket-muted/20">

                    <button
                    onClick={() => setSelectedOffer(offer)}
                    className="w-full flex items-start justify-between gap-4 py-5 text-left group">

                      <span className="font-body text-[13px] md:text-[14px] text-ticket-muted leading-[1.6] group-hover:text-ticket-fg transition-colors">
                        {offer.title}
                      </span>
                      <span className="mt-0.5 shrink-0 text-ticket-muted/60 group-hover:text-ticket-fg transition-colors">
                        <ArrowUpRight size={14} />
                      </span>
                    </button>
                  </div>
                )}
              </div>

              <ContactSheet defaultDallasLocal>
                <button className="group inline-flex items-center gap-2 px-5 py-2.5 border border-ticket-fg text-ticket-fg font-body text-[9px] font-medium tracking-[0.15em] uppercase hover:bg-ticket-fg hover:text-ticket-bg transition-colors mt-6">
                  Claim a Community Offer
                  <ArrowUpRight size={10} />
                </button>
              </ContactSheet>

              {/* Valid-thru stamp at bottom */}
              <div className="mt-8 flex items-center justify-between">
                <span className="font-body text-[8px] tracking-[0.3em] uppercase text-ticket-muted/70 text-zinc-500">
                  Valid thru 2026
                </span>
                <span className="text-ticket-muted/30 text-[10px]">✦</span>
              </div>

              {/* Fine print disclaimer */}
              <div className="mt-6 pt-4 border-t border-dashed border-ticket-muted/15">
                <p className="font-body text-[9px] leading-[1.7] text-ticket-muted/55 tracking-[0.01em] text-zinc-400">
                  All offers subject to availability and at the sole discretion of KEISH Design Studio. Pricing reflects promotional rates for Dallas-area businesses only; standard rates apply otherwise. Barter/trade agreements are evaluated on a case-by-case basis and are not guaranteed. Timelines, deliverables, and scope are finalized upon signed agreement. KEISH Design Studio reserves the right to modify, suspend, or discontinue any offer at any time without prior notice. This is not a binding contract. All work governed by our <a href="/terms" className="underline underline-offset-2 decoration-dotted hover:text-ticket-muted/70 transition-colors">Terms of Service</a>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Offer detail — drawer on mobile, modal on desktop */}
      {isMobile ? (
        <Drawer open={!!selectedOffer} onOpenChange={(open) => !open && setSelectedOffer(null)}>
          <DrawerContent className="px-6 pb-8 pt-6 max-h-[85vh] overflow-y-auto">
            <div className="ticket-card px-6 py-8 relative">
              <div className="ticket-grain" />
              <div className="relative z-10">
                {selectedOffer && <OfferDetail offer={selectedOffer} onClose={() => setSelectedOffer(null)} />}
              </div>
            </div>
          </DrawerContent>
        </Drawer>
      ) : (
        <AnimatePresence>
          {selectedOffer && (
            <OfferModal offer={selectedOffer} onClose={() => setSelectedOffer(null)} />
          )}
        </AnimatePresence>
      )}
    </motion.div>);

};

export default DallasLocalsCTA;